import React from 'react';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'antd';
import { UtensilsCrossed } from 'lucide-react';
import OrderLineItem from '../../components/orders/OrderLineItem';
import OrderSectionHeader from '../../components/orders/OrderSectionHeader';
import { normalizeDiscountBreakdown } from '../../services/helpers/OrderMath';
import {
  COUPON_TYPES,
  DeliveryProvider,
  FULFILLMENT_STATUSES,
  ORDER_METHODS,
  PaymentMethods,
} from '../../services/exports/Constants';
import CreditCardIcon from '../../resources/assets/icons/CreditCardIcon';
import PickupIcon from '../../resources/assets/icons/PickupIcon';
import DeliveryIcon from '../../resources/assets/icons/DeliveryIcon';
import FeedbackLabel from '../../components/label/FeedbackLabel';
import StarCircleIcon from '../../resources/assets/icons/StarCircleIcon';
import CashIcon from '../../resources/assets/icons/CashIcon';
import useHelpers from '../../hooks/context/useHelpers';
import DeliveryTimeDifferenceBadge from '../../components/orders/DeliveryTimeDifferenceBadge';
import CarrierIcon from '../../resources/assets/icons/CarrierIcon';
import PhoneIcon from '../../resources/assets/icons/PhoneIcon';
import useScreenType from '../../hooks/context/useScreenType';
import DeliveryProviderIcon from '../../components/delivery/DeliveryProviderIcon';
import OrderTableBox from '../../components/orders/OrderTableBox';
import { onSiteOrderTable, tableLabel } from '../../services/helpers/OnSiteOrder';

export default function OrderDetailsModal({ show = false, toggleModal, data }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Order:OrderDetailsModal' });

  const { formatCurrency, formatTime, formatDateTime } = useHelpers();
  const { isDesktop } = useScreenType();

  function renderFooter() {
    const isRejected = data.fulfillment.status === FULFILLMENT_STATUSES.rejected;

    return (
      <div
        className={classnames('w-full website-text secondary font-bold text-center py-md rounded', {
          'bg-brand-danger text-white': isRejected,
        })}
      >
        {t(`fulfillment_statuses.${data.fulfillment.status}`, t('fulfillment_statuses.default'))}
      </div>
    );
  }

  const renderPaymentInfo = () => {
    if (data.payment.payment_method_id === PaymentMethods.CASH) {
      return (
        <div className="mt-6 website-text flex">
          <CashIcon className="my-auto h-6 w-6" />
          <div className="my-auto ml-2">
            {t(data.company.overwrite_cash_payments_to_invoice ? 'labels.invoice_payment' : 'labels.paid_in_cash')}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-6 website-text-mini secondary flex">
        <CreditCardIcon width={28} height={20} className="card-icon" />
        <div className="mt-auto ml-2">{t('labels.customer_paid_online')}</div>
      </div>
    );
  };

  const renderOrderMethodIcon = () => {
    if (data.method === ORDER_METHODS.delivery && data.delivery_provider !== DeliveryProvider.Standalone) {
      return <DeliveryProviderIcon provider={data.delivery_provider} className="h-12 w-12" />;
    }

    const icon = {
      // lucide UtensilsCrossed, as in the shop.
      [ORDER_METHODS.on_site]: <UtensilsCrossed className="h-[22px] w-[22px] text-white" aria-hidden="true" />,
      [ORDER_METHODS.pickup]: <PickupIcon className="h-7 w-7" />,
      [ORDER_METHODS.delivery]: <DeliveryIcon className="h-7 w-7" />,
    }[data.method];

    return <div className="h-14 w-14 bg-gray-800 rounded-full flex items-center justify-center">{icon}</div>;
  };

  const renderOrderMethodLabel = () =>
    ({
      [ORDER_METHODS.on_site]: t(`order_methods.${data.method}`),
      [ORDER_METHODS.pickup]: t(`order_methods.${data.method}`),
      [ORDER_METHODS.delivery]: t(`order_methods.${data.method}`),
    })[data.method];

  // A table order: its table line as placed in the grey box under the guest's name, no address. An
  // order placed with Room/Table service keeps the venue and the number the guest typed.
  const renderOnSiteTable = () => {
    const { line, venue, typed } = onSiteOrderTable(data);

    if (typed) {
      return (
        <>
          {venue && (
            <div className="website-text mt-1">
              <b>{t('labels.venue')}:</b> {venue}
            </div>
          )}
          <div className="website-text mt-1">
            <b>{t('labels.table_number')}:</b> {typed}
          </div>
        </>
      );
    }

    return line ? (
      <OrderTableBox>{tableLabel(line, (name) => t('labels.table_numbered', { name }))}</OrderTableBox>
    ) : null;
  };

  return (
    <Drawer
      id="order-details-modal"
      open={show}
      alignment="center"
      scrollable
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      onClose={toggleModal}
      footer={renderFooter()}
      title={t('title')}
      classNames={{
        body: '!overflow-x-hidden',
      }}
    >
      <div className="grid grid-cols-12 gap-6 w-full">
        <div className="col-span-8 w-full break-words">
          <div>
            <h5 className="mr-2">#{data.fulfillment.number}</h5>
            <div className="website-text font-bold">
              {data?.customer_first_name} {data?.customer_last_name}
            </div>
          </div>
          {data?.method === ORDER_METHODS.delivery && (
            <>
              <div className="website-text mt-1">
                {data?.delivery_street_name} {data?.delivery_street_number},
              </div>
              <div className="website-text mt-1">
                {data?.delivery_zip_code} {data?.delivery_city}
              </div>
              {data?.delivery_doorbell_name && <div className="website-text mt-1">{data?.delivery_doorbell_name}</div>}
              {data?.delivery_company_name && <div className="website-text mt-1">{data?.delivery_company_name}</div>}
            </>
          )}
          {data?.method === ORDER_METHODS.on_site && renderOnSiteTable()}
          <div className="website-text mt-1">
            <b>{t('labels.phone')}:</b> {data?.customer_phone_number}
          </div>
          {data?.customer_email ? (
            <div className="website-text mt-1">
              <b>{t('labels.email')}:</b> {data?.customer_email}
            </div>
          ) : null}
        </div>
        <div className="col-span-4 w-full break-words">
          <div>
            <div className="flex justify-center">{renderOrderMethodIcon()}</div>
            <div className="website-text-mini secondary w-full text-center">{renderOrderMethodLabel()}</div>
            <div className="website-text font-bold text-white w-full text-center bg-black mt-1">
              {data?.scheduled_for ? formatTime(data.fulfillment.estimated_pickup_time) : 'ASAP'}
            </div>
            <DeliveryTimeDifferenceBadge
              initial={data.delivery?.initial_pickup_estimate}
              actual={data.delivery?.pickup_estimate}
              className="w-full mt-2"
            />
          </div>
        </div>
      </div>
      {data?.coupon_type === COUPON_TYPES.REWARD && (
        <FeedbackLabel
          message={
            <div className="flex">
              <StarCircleIcon className="mr-2 my-auto" />
              <div className="website-text my-auto">{t('labels.points_used')}</div>
            </div>
          }
          type="warning"
          className="mt-4"
        />
      )}
      {data.scheduled_for && data.fulfillment.status !== FULFILLMENT_STATUSES.preparing && (
        <div
          className={classnames(
            '-mx-6 px-6 mt-5 py-4 relative',
            data.fulfillment.status === FULFILLMENT_STATUSES.accepted ? 'bg-[#F89A2A]' : 'bg-[#2F80ED]',
          )}
        >
          <h5 className="text-white text-center">
            {t('schedule.header', {
              date: moment(data.fulfillment.estimated_pickup_time).isSame(moment(), 'day')
                ? t('labels.today')
                : formatDateTime(data.fulfillment.estimated_pickup_time, { format: 'dddd, DD MMM' }),
              time: formatTime(data.fulfillment.estimated_pickup_time),
            })}
          </h5>
          <div className="website-text text-center text-white break-word">{t('schedule.text')}</div>
        </div>
      )}
      {data?.delivery?.runner_name ? (
        <div>
          <OrderSectionHeader className="mt-5">{t('sections.driver_info')}</OrderSectionHeader>
          <p className="mb-0 mt-4">
            <CarrierIcon className="mr-2" />
            <b>{t('labels.driver_name')}</b>
            {': '}
            {data?.delivery?.runner_name}
          </p>
          {data?.delivery?.runner_phone_number ? (
            <p className="mb-0 mt-2">
              <PhoneIcon className="mr-2" />
              <b>{t('labels.driver_phone_number')}</b>
              {': '}
              {data?.delivery?.runner_phone_number}
              {Boolean(data?.delivery?.runner_phone_pin_code) && (
                <>
                  {' ('}
                  <b>{t('labels.pin_code')}</b>
                  {': '}
                  {data?.delivery?.runner_phone_pin_code})
                </>
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      <OrderSectionHeader className="mt-4">{t('sections.order_info')}</OrderSectionHeader>
      {data?.line_items?.map((item, idx) => (
        <OrderLineItem
          lineItem={item}
          isLastItem={data?.line_items?.length ? idx === data.line_items.length - 1 : false}
          key={idx}
        />
      ))}
      {data?.customer_note && (
        <p className="website-text mb-0 mt-4 break-words">
          <b>{t('labels.order_note')}</b>
          {': '}
          {data.customer_note}
        </p>
      )}
      <OrderSectionHeader className="mt-5">{t('sections.price_info')}</OrderSectionHeader>
      <div className="flex justify-between website-text mt-4">
        <div>{t('labels.original')}</div>
        <div>{formatCurrency(data?.sub_total, data?.currency)}</div>
      </div>
      {data?.method === ORDER_METHODS.delivery && data?.method_fee > 0 && (
        <div className="flex justify-between website-text mt-4">
          <div>{t('labels.delivery_fee')}</div>
          <div>{formatCurrency(data?.method_fee, data?.currency)}</div>
        </div>
      )}
      {data?.tip > 0 && (
        <div className="flex justify-between website-text mt-4">
          <div>{t('labels.tip')}</div>
          <div>{formatCurrency(data?.tip, data?.currency)}</div>
        </div>
      )}
      {data?.service_fee > 0 && (
        <div className="flex justify-between website-text mt-4">
          <div>{t('labels.service_fee')}</div>
          <div>{formatCurrency(data?.service_fee, data?.currency)}</div>
        </div>
      )}
      {data?.payment_method_upcharge_fee > 0 && (
        <div className="flex justify-between website-text mt-4">
          <div>{t(`labels.payment_method_upcharge_fee.${data.payment.payment_method_id}`)}</div>
          <div>{formatCurrency(data.payment_method_upcharge_fee)}</div>
        </div>
      )}
      {data?.total_discount > 0 && <DiscountBreakdown data={data} />}
      {data?.additional_tax > 0 && (
        <div className="flex justify-between website-text mt-4">
          <div>{t('labels.tax')}</div>
          <div>{formatCurrency(data?.additional_tax, data?.currency)}</div>
        </div>
      )}
      <div className="flex justify-between website-text font-bold mt-4">
        <div>{t('labels.total')}</div>
        <div>{formatCurrency(data?.total, data?.currency)}</div>
      </div>
      {renderPaymentInfo()}
      {data.payment.adjustments.length > 0 && (
        <>
          <OrderSectionHeader className="mt-5">{t('sections.adjustments')}</OrderSectionHeader>
          {data.payment.adjustments.map((item) => (
            <div key={item.id}>
              <div className="flex justify-between website-text bold mt-4">
                <div className="bold">{formatDateTime(item?.paid_at)}</div>
                <div className="bold">{formatCurrency(item.total)}</div>
              </div>
              <div className="website-text-mini mt-2 text-decoration-underline">
                {t(`adjustment_reasons.${item.adjustment_reason}`, item.adjustment_reason)}
              </div>
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}

function DiscountBreakdown({ data }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Order:OrderDetailsModal' });
  const { formatCurrency } = useHelpers();

  const breakdown = normalizeDiscountBreakdown(data);

  if (!breakdown) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between website-text mt-4">
        <div>{t('labels.discount')}</div>
        <div>-{formatCurrency(breakdown.total, breakdown.currency)}</div>
      </div>
      {breakdown.items.map((item) => (
        <div className="flex justify-between text-xs mt-4 ml-6" key={item.key}>
          <div>{t(`labels.discount_breakdown.${item.key}`)}</div>
          <div>-{formatCurrency(item.amount, breakdown.currency)}</div>
        </div>
      ))}
    </div>
  );
}
